import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Select, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import moment from 'moment';
import { isEqual, omit } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import Typography from '@mui/material/Typography';
import { KeyedMutator } from 'swr/_internal';
import {
  AccountData,
  AccountService,
  OrderingCutoffSettingsData,
  OrderingCutoffStrategy,
  OrderPageData,
  OrderPageImageData,
  OrderPageTypes,
  ProjectSelectionConfigData,
  ProjectSelectionCount,
  ProjectSelectionMode,
  SaveCardConfigData,
  UpdateOrderPageInput,
} from '../../../api';
import { OrderPageUpdate } from '../../OrderPageSettingsPage';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { useAuth } from '../../../session/InternalAuthProvider';
import { getPassOrderPages } from '../../../utils/AccountUtils';
import { FormFieldColorPicker } from '../../formComponents/FormFieldColorPicker';
import { FormTextField } from '../../formComponents/FormTextField';
import { SettingsRow } from '../../formComponents/SettingsRow';
import { LocationPicker } from './LocationPicker';
import { FormCheckbox } from '../../formComponents/FormCheckBox';
import { FormLabelField } from '../../formComponents/FormLabelField';
import { ProviderCapabilityGate } from '../../../common/ui/ProviderCapabilityGate';
import { SaveCardConsentVariant, withDefaultSaveCardLabel } from '../../../common/payments/saveCardDefaults';
import { FormTimePicker } from '../../formComponents/FormTimePicker';
import SharedMobileTimePicker from '../../../common/ui/SharedMobileTimePicker';
import { OrderPageImagesPanel } from './OrderPageImagesPanel';
import {
  describeReschedulePolicyOverride,
  ReschedulePolicyOverrideFormValues,
  ReschedulePolicyOverrideRow,
  reschedulePolicyOverrideSchema,
  toReschedulePolicyFormValues,
  toReschedulePolicyPayload,
} from './ReschedulePolicyOverrideRow';
import { SectionSaveRegistrar } from '../../saveHeader/SectionSaveRegistrar';
import {
  collect,
  diffBoolean,
  diffCount,
  diffDurationMinutes,
  diffEnum,
  diffLocation,
  diffNumber,
  diffText,
  diffTimeOfDay,
  diffTimeOfDaySeconds,
} from '../../saveHeader/diffBuilders';
import { useLanguage } from '../../../common/GeneralUtils';
import type { ChangeEntry } from '../../saveHeader/SaveHeaderContext';
import { SettingsPageTabs } from '../../SettingsPageTabs';
import { DEFAULT_ORDER_PAGE_COLOR } from '../../../common/theme/mbTokens';

type SettingsSectionProps = {
  orderPage: OrderPageData;
  orderPageInitialValues: OrderPageUpdate;
  updateLocalAccount: KeyedMutator<AccountData>;
};

export type LowParticipationAction = 'auto_cancel' | 'notify';

export type SettingsFormValues = {
  color: string;
  duration: number;
  ordering_cutoff_settings?: OrderingCutoffSettingsData;
  max_date_in_days: number;
  after_buffer_minutes: number;
  order_page_images?: OrderPageImageData[];
  location_id?: string;
  wait_list_enabled?: boolean;
  contact_details_optional_in_backoffice: boolean;
  default_time?: number;
  default_tickets_amount?: number;
  pass_customer_cancel_hours_before?: number;
  min_participants_enabled: boolean;
  min_participants_threshold?: number | null;
  low_participation_action: LowParticipationAction;
  low_participation_hours?: number | null;
  save_card_config?: SaveCardConfigData;
  project_selection_config?: ProjectSelectionConfigData;
} & ReschedulePolicyOverrideFormValues;

export const SettingsSection = ({ orderPage, orderPageInitialValues, updateLocalAccount }: SettingsSectionProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { readOnly } = UseAuthPermissions();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const posthog = usePostHog();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isEventOrderPage = orderPage.order_page_type === OrderPageTypes.EVENT;
  const isCourseOrderPage = orderPage.order_page_type === OrderPageTypes.COURSE;
  const isUsedInPass = getPassOrderPages(account, true).some((pass) =>
    pass.redeemable_on?.some((r) => r.order_page_id === orderPage.id),
  );
  const showAutoCancel = isUsedInPass && !isCourseOrderPage;
  // The API tolerates a missing label, but a consent checkbox with no text is not a
  // consent checkbox -- require one per customer-facing language when the toggle is on.
  const saveCardLabelShape = Object.fromEntries(
    (account.customer_facing_widgets_languages || []).map((lang) => [
      lang,
      yup.string().trim().required(t('editOrderPage.yup.required')),
    ]),
  );
  const schema = {
    color: yup.string().required(t('editOrderPage.yup.required')),
    save_card_config: yup.object().shape({
      enabled: yup.boolean(),
      label: yup.object().when('enabled', {
        is: true,
        then: (labelSchema: any) => labelSchema.shape(saveCardLabelShape),
      }),
    }),
    duration: yup.number().required(t('editOrderPage.yup.required')).min(30, t('editOrderPage.yup.hasToBeMoreThan30')),
    project_selection_config: yup.object().shape({
      lock_hours_before_start: yup
        .number()
        .min(0, t('editOrderPage.yup.hasToBePoitive'))
        .when('mode', {
          is: (mode: string) => mode && mode !== ProjectSelectionMode.DISABLED,
          then: (lockHoursSchema) => lockHoursSchema.required(t('editOrderPage.yup.required')),
        }),
    }),
    reschedule_policy: reschedulePolicyOverrideSchema(t),
    min_participants_threshold: yup
      .number()
      .nullable()
      .when('min_participants_enabled', {
        is: true,
        then: (s) =>
          s
            .required(t('editOrderPage.yup.required'))
            .min(1, t('editOrderPage.yup.hasToBeMoreThan1'))
            .integer(t('editOrderPage.yup.required')),
      }),
    low_participation_hours: yup
      .number()
      .nullable()
      .when('min_participants_enabled', {
        is: true,
        then: (s) =>
          s
            .required(t('editOrderPage.yup.required'))
            .positive(t('editOrderPage.yup.hasToBePoitive'))
            .integer(t('editOrderPage.yup.required'))
            .max(168, t('editOrderPage.yup.mustBeAtMost7Days')),
      }),
  };
  const privateOrderSpecificSchema = {
    max_date_in_days: yup
      .number()
      .required(t('editOrderPage.yup.required'))
      .positive(t('editOrderPage.yup.hasToBePoitive'))
      .lessThan(181, t('editOrderPage.yup.hasToBeLessThan180')),
    after_buffer_minutes: yup
      .number()
      .required(t('editOrderPage.yup.required'))
      .min(0, t('editOrderPage.yup.hasToBePoitive')),
  };
  const eventsSpecificSchema = {
    default_time: yup.number().min(0, t('editOrderPage.yup.hasToBePoitive')).nullable(),
    default_tickets_amount: yup.number().min(1, t('editOrderPage.yup.hasToBeMoreThan1')).required(),
  };
  const validationSchema = yup
    .object()
    .shape(isEventOrderPage ? { ...schema, ...eventsSpecificSchema } : { ...schema, ...privateOrderSpecificSchema });

  const initialValues: SettingsFormValues = {
    color: orderPage?.color || DEFAULT_ORDER_PAGE_COLOR,
    duration: (orderPage?.duration || 0) / 60,
    ordering_cutoff_settings: orderPage.ordering_cutoff_settings || {
      strategy: OrderingCutoffStrategy.DAILY_CUTOFF,
      minimum_days_ahead: 0,
      cutoff_time_hour: 18,
      cutoff_time_minute: 0,
    },
    max_date_in_days: orderPage.max_date_in_days,
    after_buffer_minutes: orderPage.after_buffer_minutes,
    order_page_images: orderPage.order_page_images || [],
    location_id: orderPage.location_id,
    wait_list_enabled: orderPage.waiting_list?.enabled,
    contact_details_optional_in_backoffice: !!orderPage.contact_details_optional_in_backoffice,
    default_time: orderPage.default_time,
    default_tickets_amount: orderPage.default_tickets_amount,
    pass_customer_cancel_hours_before: orderPage.pass_customer_cancel_hours_before,
    min_participants_enabled: showAutoCancel && (orderPage.min_participants_threshold ?? 0) > 0,
    min_participants_threshold: orderPage.min_participants_threshold,
    low_participation_action:
      (orderPage.notify_hours_before_under_min ?? 0) > 0 && !((orderPage.auto_cancel_hours_before ?? 0) > 0)
        ? 'notify'
        : 'auto_cancel',
    low_participation_hours: orderPage.auto_cancel_hours_before ?? orderPage.notify_hours_before_under_min ?? undefined,
    save_card_config: {
      enabled: orderPage.save_card_config?.enabled ?? false,
      // Prefilled as the baseline rather than on toggle, so the consent text is already
      // there the moment the merchant ticks the box -- and the change dialog then reports
      // only the toggle, which is the decision they actually made.
      label: withDefaultSaveCardLabel(
        orderPage.save_card_config?.label,
        account.customer_facing_widgets_languages,
        SaveCardConsentVariant.ORDER,
      ),
    },
    project_selection_config: {
      mode: orderPage.project_selection_config?.mode ?? ProjectSelectionMode.DISABLED,
      selection_count: orderPage.project_selection_config?.selection_count ?? ProjectSelectionCount.SINGLE,
      lock_hours_before_start: orderPage.project_selection_config?.lock_hours_before_start ?? 48,
    },
    reschedule_policy_override: !!orderPage.reschedule_policy,
    reschedule_policy: toReschedulePolicyFormValues(orderPage.reschedule_policy ?? account.reschedule_policy),
  };

  const handleSubmit = async (values: SettingsFormValues, { setStatus }: FormikHelpers<SettingsFormValues>) => {
    try {
      posthog.capture('Editing settings', { section: 'settingsSection', settings: values });
      const autoCancelOverride = showAutoCancel
        ? {
            min_participants_threshold: values.min_participants_enabled ? values.min_participants_threshold : 0,
            auto_cancel_hours_before:
              values.min_participants_enabled && values.low_participation_action === 'auto_cancel'
                ? values.low_participation_hours
                : null,
            notify_hours_before_under_min:
              values.min_participants_enabled && values.low_participation_action === 'notify'
                ? values.low_participation_hours
                : null,
          }
        : {};
      const updatedAccount = await AccountService.updateOrderPage(
        omit(
          {
            ...orderPageInitialValues,
            ...values,
            ...autoCancelOverride,
            reschedule_policy: toReschedulePolicyPayload(values),
            id: orderPage.id,
            duration: (values.duration || 0) * 60,
          },
          'availability_settings.restrictions',
          'objects',
          'min_participants_enabled',
          'low_participation_action',
          'low_participation_hours',
          'reschedule_policy_override',
        ) as UpdateOrderPageInput,
      );
      await updateLocalAccount(updatedAccount);
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const getChanges = (v: SettingsFormValues, iv: SettingsFormValues): ChangeEntry[] =>
    collect(
      diffLocation(t('locationPicker.location'), iv.location_id, v.location_id, account, language),
      diffText(t('editOrderPage.orderPageColor'), iv.color, v.color),
      diffCount(t('editOrderPage.galleryImages'), iv.order_page_images?.length, v.order_page_images?.length, t),
      diffEnum(
        t('editOrderPage.orderingCutoffSettings.strategy'),
        iv.ordering_cutoff_settings?.strategy,
        v.ordering_cutoff_settings?.strategy,
        (val) => {
          if (val === OrderingCutoffStrategy.DAILY_CUTOFF) {
            return t('editOrderPage.orderingCutoffSettings.dailyCutoff');
          }
          if (val === OrderingCutoffStrategy.HOURS_BEFORE_START) {
            return t('editOrderPage.orderingCutoffSettings.hoursBeforeStart');
          }
          if (val === OrderingCutoffStrategy.ALLOW_ON_THE_DAY_ORDERS) {
            return t('editOrderPage.orderingCutoffSettings.allowSameDayOrders');
          }
          return val ?? '';
        },
      ),
      diffNumber(
        t('editOrderPage.orderingCutoffSettings.mustBook'),
        iv.ordering_cutoff_settings?.minimum_days_ahead,
        v.ordering_cutoff_settings?.minimum_days_ahead,
      ),
      diffTimeOfDay(
        t('editOrderPage.orderingCutoffSettings.dailyCutoff'),
        iv.ordering_cutoff_settings
          ? {
              hour: iv.ordering_cutoff_settings.cutoff_time_hour,
              minute: iv.ordering_cutoff_settings.cutoff_time_minute,
            }
          : undefined,
        v.ordering_cutoff_settings
          ? {
              hour: v.ordering_cutoff_settings.cutoff_time_hour,
              minute: v.ordering_cutoff_settings.cutoff_time_minute,
            }
          : undefined,
      ),
      diffNumber(
        t('editOrderPage.orderingCutoffSettings.hoursBeforeStart'),
        iv.ordering_cutoff_settings?.hours_before_start,
        v.ordering_cutoff_settings?.hours_before_start,
      ),
      diffNumber(t('editOrderPage.orderingCutoffSettings.maxBookingWindow'), iv.max_date_in_days, v.max_date_in_days),
      diffDurationMinutes(t('editOrderPage.orderPageBuffer'), iv.after_buffer_minutes, v.after_buffer_minutes, t),
      diffDurationMinutes(t('editOrderPage.orderPageBufferDuration'), iv.duration, v.duration, t),
      diffBoolean(t('editOrderPage.waitlist'), iv.wait_list_enabled, v.wait_list_enabled),
      diffBoolean(
        t('editOrderPage.contactDetailsOptionalInBackoffice'),
        iv.contact_details_optional_in_backoffice,
        v.contact_details_optional_in_backoffice,
      ),
      diffBoolean(t('editOrderPage.saveCard.title'), iv.save_card_config?.enabled, v.save_card_config?.enabled),
      ...(account.customer_facing_widgets_languages || []).map((lang) =>
        diffText(
          `${t('editOrderPage.saveCard.consentText')} (${lang})`,
          iv.save_card_config?.label?.[lang],
          v.save_card_config?.label?.[lang],
        ),
      ),
      diffEnum(
        t('editOrderPage.projectSelection.mode'),
        iv.project_selection_config?.mode,
        v.project_selection_config?.mode,
        (val) => {
          if (val === ProjectSelectionMode.POST_BOOKING) {
            return t('editOrderPage.projectSelection.modePostBooking');
          }
          if (val === ProjectSelectionMode.POST_PAYMENT) {
            return t('editOrderPage.projectSelection.modePostPayment');
          }
          if (val === ProjectSelectionMode.IN_BOOKING_OPTIONAL) {
            return t('editOrderPage.projectSelection.modeInBookingOptional');
          }
          if (val === ProjectSelectionMode.IN_BOOKING_REQUIRED) {
            return t('editOrderPage.projectSelection.modeInBookingRequired');
          }
          return t('editOrderPage.projectSelection.modeDisabled');
        },
      ),
      diffEnum(
        t('editOrderPage.projectSelection.count'),
        iv.project_selection_config?.selection_count,
        v.project_selection_config?.selection_count,
        (val) =>
          val === ProjectSelectionCount.PER_PARTICIPANT
            ? t('editOrderPage.projectSelection.countPerParticipant')
            : t('editOrderPage.projectSelection.countSingle'),
      ),
      diffNumber(
        t('editOrderPage.projectSelection.lockHours'),
        iv.project_selection_config?.lock_hours_before_start,
        v.project_selection_config?.lock_hours_before_start,
      ),
      diffTimeOfDaySeconds(t('editOrderPage.defaultTime'), iv.default_time, v.default_time),
      diffNumber(t('editOrderPage.defaultTicketsAmount'), iv.default_tickets_amount, v.default_tickets_amount),
      isEqual(
        { override: iv.reschedule_policy_override, policy: iv.reschedule_policy },
        { override: v.reschedule_policy_override, policy: v.reschedule_policy },
      )
        ? null
        : {
            label: t('tabs.reschedulepolicy'),
            from: describeReschedulePolicyOverride(iv, t),
            to: describeReschedulePolicyOverride(v, t),
          },
      diffNumber(
        t('editOrderPage.passCustomerCancel.title'),
        iv.pass_customer_cancel_hours_before,
        v.pass_customer_cancel_hours_before,
      ),
      diffNumber(
        t('editOrderPage.lowParticipationAutoCancel.minimumLabel'),
        iv.min_participants_threshold,
        v.min_participants_enabled ? v.min_participants_threshold : 0,
      ),
      diffNumber(
        t('editOrderPage.lowParticipationAutoCancel.cancelHoursLabel'),
        orderPage.auto_cancel_hours_before,
        v.min_participants_enabled && v.low_participation_action === 'auto_cancel' ? v.low_participation_hours : null,
      ),
      diffNumber(
        t('editOrderPage.lowParticipationAutoCancel.notifyHoursLabel'),
        orderPage.notify_hours_before_under_min,
        v.min_participants_enabled && v.low_participation_action === 'notify' ? v.low_participation_hours : null,
      ),
    );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {(formik) => {
        const { values, isSubmitting, setFieldValue } = formik;
        const formIsDisabled = readOnly || isSubmitting;
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar tabKey={SettingsPageTabs.SETTINGS} getChanges={getChanges} disabled={readOnly} />
            <Grid
              container
              flexDirection={isSmallScreen ? 'column-reverse' : 'row'}
              justifyContent={isSmallScreen ? undefined : 'space-between'}
              flexWrap="wrap"
              alignItems="flex-start">
              <Grid container flexDirection="column" flexWrap="nowrap">
                <SettingsRow label={t('editOrderPage.address')}>
                  <LocationPicker
                    initialLocationId={initialValues.location_id}
                    onSelected={(selectedLocation) => setFieldValue('location_id', selectedLocation)}
                    withoutLabel
                  />
                </SettingsRow>
                <SettingsRow label={t('editOrderPage.orderPageColor')}>
                  <FormFieldColorPicker
                    tooltip={t('editOrderPage.orderPageColortooltip')}
                    disabled={formIsDisabled}
                    fieldName="color"
                  />
                </SettingsRow>
                <SettingsRow label={t('editOrderPage.galleryImages')}>
                  <OrderPageImagesPanel
                    images={values.order_page_images || []}
                    onChange={(images) => setFieldValue('order_page_images', images)}
                    disabled={formIsDisabled}
                  />
                </SettingsRow>
                <SettingsRow label={t('editOrderPage.orderingCutoffSettings.title')}>
                  <Grid container gap={2} alignItems="center" flexDirection="column">
                    <Grid container gap={1} alignItems="center">
                      <Grid item>
                        <Typography>{t('editOrderPage.orderingCutoffSettings.strategy')}</Typography>
                      </Grid>
                      <Grid item>
                        <Select
                          value={values.ordering_cutoff_settings?.strategy || OrderingCutoffStrategy.DAILY_CUTOFF}
                          onChange={(e) => setFieldValue('ordering_cutoff_settings.strategy', e.target.value)}
                          disabled={formIsDisabled}
                          size="medium">
                          <MenuItem value={OrderingCutoffStrategy.DAILY_CUTOFF}>
                            {t('editOrderPage.orderingCutoffSettings.dailyCutoff')}
                          </MenuItem>
                          <MenuItem value={OrderingCutoffStrategy.HOURS_BEFORE_START}>
                            {t('editOrderPage.orderingCutoffSettings.hoursBeforeStart')}
                          </MenuItem>
                          <MenuItem value={OrderingCutoffStrategy.ALLOW_ON_THE_DAY_ORDERS}>
                            {t('editOrderPage.orderingCutoffSettings.allowSameDayOrders')}
                          </MenuItem>
                        </Select>
                      </Grid>
                    </Grid>

                    {values.ordering_cutoff_settings?.strategy === OrderingCutoffStrategy.DAILY_CUTOFF && (
                      <Grid container gap={1} alignItems="center">
                        <Grid item>
                          <Typography>{t('editOrderPage.orderingCutoffSettings.mustBook')}</Typography>
                        </Grid>
                        <Grid item>
                          <FormTextField
                            isFieldUpdate
                            type="number"
                            width={60}
                            fieldName="ordering_cutoff_settings.minimum_days_ahead"
                            disabled={formIsDisabled}
                          />
                        </Grid>
                        <Grid item>
                          <Typography>{t('editOrderPage.orderingCutoffSettings.daysAheadDailyCutoffAt')}</Typography>
                        </Grid>
                        <Grid item>
                          <SharedMobileTimePicker
                            value={moment().set({
                              hour: values.ordering_cutoff_settings?.cutoff_time_hour || 18,
                              minute: values.ordering_cutoff_settings?.cutoff_time_minute || 0,
                              second: 0,
                              millisecond: 0,
                            })}
                            onChange={(newTime) => {
                              if (newTime) {
                                void setFieldValue('ordering_cutoff_settings.cutoff_time_hour', newTime.hour());
                                void setFieldValue('ordering_cutoff_settings.cutoff_time_minute', newTime.minute());
                              }
                            }}
                            disabled={formIsDisabled}
                            minutesStep={15}
                          />
                        </Grid>
                      </Grid>
                    )}

                    {values.ordering_cutoff_settings?.strategy === OrderingCutoffStrategy.HOURS_BEFORE_START && (
                      <Grid container gap={1} alignItems="center">
                        <Grid item>
                          <Typography>{t('editOrderPage.orderingCutoffSettings.bookingCloses')}</Typography>
                        </Grid>
                        <Grid item>
                          <FormTextField
                            isFieldUpdate
                            type="number"
                            width={60}
                            fieldName="ordering_cutoff_settings.hours_before_start"
                            disabled={formIsDisabled}
                          />
                        </Grid>
                        <Grid item>
                          <Typography>{t('editOrderPage.orderingCutoffSettings.hoursBeforeWorkshopStarts')}</Typography>
                        </Grid>
                      </Grid>
                    )}

                    {values.ordering_cutoff_settings?.strategy === OrderingCutoffStrategy.ALLOW_ON_THE_DAY_ORDERS && (
                      <Grid container gap={1} alignItems="center">
                        <Grid item>
                          <Typography>{t('editOrderPage.orderingCutoffSettings.customersCanBookSameDay')}</Typography>
                        </Grid>
                      </Grid>
                    )}

                    <Grid container gap={1} alignItems="center">
                      <Grid item>
                        <Typography>{t('editOrderPage.orderingCutoffSettings.maxBookingWindow')}</Typography>
                      </Grid>
                      <Grid item>
                        <FormTextField
                          isFieldUpdate
                          width={70}
                          fieldName="max_date_in_days"
                          disabled={formIsDisabled}
                          type="number"
                        />
                      </Grid>
                      <Grid item>
                        <Typography>{t('editOrderPage.orderingCutoffSettings.daysAhead')}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </SettingsRow>
                {!isCourseOrderPage && <ReschedulePolicyOverrideRow account={account} disabled={formIsDisabled} />}
                {isUsedInPass && (
                  <SettingsRow label={t('editOrderPage.passCustomerCancel.title')}>
                    <Grid container gap={1} alignItems="center">
                      <Grid item>
                        <Typography>{t('editOrderPage.passCustomerCancel.cancelUpTo')}</Typography>
                      </Grid>
                      <Grid item>
                        <FormTextField
                          isFieldUpdate
                          type="number"
                          width={70}
                          fieldName="pass_customer_cancel_hours_before"
                          disabled={formIsDisabled}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>{t('editOrderPage.passCustomerCancel.hoursBeforeStart')}</Typography>
                      </Grid>
                    </Grid>
                  </SettingsRow>
                )}
                {showAutoCancel && (
                  <SettingsRow label={t('editOrderPage.lowParticipationAutoCancel.title')}>
                    <Grid container gap={2} flexDirection="column" alignItems="flex-start">
                      <FormCheckbox
                        fieldName="min_participants_enabled"
                        disabled={formIsDisabled}
                        isFieldUpdate={false}
                        label={t('editOrderPage.lowParticipationAutoCancel.requireMinimum')}
                        onChange={(e) => {
                          const { checked } = e.target as HTMLInputElement;
                          void setFieldValue('min_participants_enabled', checked);
                          if (!checked) {
                            void setFieldValue('min_participants_threshold', undefined);
                            void setFieldValue('low_participation_hours', undefined);
                          }
                        }}
                      />
                      {values.min_participants_enabled && (
                        <>
                          <Grid container gap={1} alignItems="center">
                            <Grid item>
                              <Typography>{t('editOrderPage.lowParticipationAutoCancel.minimumLabel')}</Typography>
                            </Grid>
                            <Grid item>
                              <FormTextField
                                isFieldUpdate
                                type="number"
                                width={70}
                                fieldName="min_participants_threshold"
                                disabled={formIsDisabled}
                              />
                            </Grid>
                          </Grid>
                          <Grid container gap={1} flexDirection="column" alignItems="flex-start">
                            <Typography>{t('editOrderPage.lowParticipationAutoCancel.actionLabel')}</Typography>
                            <RadioGroup
                              value={values.low_participation_action}
                              onChange={(e) =>
                                setFieldValue('low_participation_action', e.target.value as LowParticipationAction)
                              }>
                              <FormControlLabel
                                value="auto_cancel"
                                control={<Radio />}
                                disabled={formIsDisabled}
                                label={t('editOrderPage.lowParticipationAutoCancel.actionAutoCancel')}
                              />
                              <FormControlLabel
                                value="notify"
                                control={<Radio />}
                                disabled={formIsDisabled}
                                label={t('editOrderPage.lowParticipationAutoCancel.actionNotify')}
                              />
                            </RadioGroup>
                          </Grid>
                          <Grid container gap={1} alignItems="center">
                            <Grid item>
                              <FormTextField
                                isFieldUpdate
                                type="number"
                                width={70}
                                fieldName="low_participation_hours"
                                disabled={formIsDisabled}
                              />
                            </Grid>
                            <Grid item>
                              <Typography>{t('editOrderPage.lowParticipationAutoCancel.hoursBefore')}</Typography>
                            </Grid>
                          </Grid>
                          <Typography fontSize="14px" color="text.secondary">
                            {t('editOrderPage.lowParticipationAutoCancel.windowHint')}
                          </Typography>
                        </>
                      )}
                    </Grid>
                  </SettingsRow>
                )}
                <SettingsRow label={t('editOrderPage.saveCard.title')}>
                  <ProviderCapabilityGate capability="saved_cards">
                    {(capabilityDisabled) => (
                      <Grid container gap={1} flexDirection="column" alignItems="flex-start">
                        {/* Benefit and downside both sit above the toggle: a caution
                                  only visible once the feature is on is no caution at all. */}
                        <Typography variant="body2" color="text.secondary">
                          {t('editOrderPage.saveCard.description')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('editOrderPage.saveCard.caution')}
                        </Typography>
                        <FormCheckbox
                          fieldName="save_card_config.enabled"
                          disabled={formIsDisabled || capabilityDisabled}
                          isFieldUpdate
                          label={t('editOrderPage.saveCard.enable')}
                        />
                        {values.save_card_config?.enabled && (
                          <>
                            <FormLabelField
                              labelFieldName="save_card_config.label"
                              label={t('editOrderPage.saveCard.consentText')}
                              disabled={formIsDisabled || capabilityDisabled}
                            />
                            <Typography fontSize="14px" color="text.secondary">
                              {t('editOrderPage.saveCard.hint')}
                            </Typography>
                          </>
                        )}
                      </Grid>
                    )}
                  </ProviderCapabilityGate>
                </SettingsRow>
                <SettingsRow label={t('editOrderPage.projectSelection.title')}>
                  <Grid container gap={1} flexDirection="column" alignItems="flex-start">
                    <Typography variant="body2" color="text.secondary">
                      {t('editOrderPage.projectSelection.description')}
                    </Typography>
                    <Grid container gap={1} alignItems="center">
                      <Typography>{t('editOrderPage.projectSelection.mode')}</Typography>
                      <Select
                        value={values.project_selection_config?.mode || ProjectSelectionMode.DISABLED}
                        onChange={(e) => setFieldValue('project_selection_config.mode', e.target.value)}
                        disabled={formIsDisabled}
                        size="medium">
                        <MenuItem value={ProjectSelectionMode.DISABLED}>
                          {t('editOrderPage.projectSelection.modeDisabled')}
                        </MenuItem>
                        <MenuItem value={ProjectSelectionMode.POST_BOOKING}>
                          {t('editOrderPage.projectSelection.modePostBooking')}
                        </MenuItem>
                        <MenuItem value={ProjectSelectionMode.POST_PAYMENT}>
                          {t('editOrderPage.projectSelection.modePostPayment')}
                        </MenuItem>
                        <MenuItem value={ProjectSelectionMode.IN_BOOKING_OPTIONAL}>
                          {t('editOrderPage.projectSelection.modeInBookingOptional')}
                        </MenuItem>
                        <MenuItem value={ProjectSelectionMode.IN_BOOKING_REQUIRED}>
                          {t('editOrderPage.projectSelection.modeInBookingRequired')}
                        </MenuItem>
                      </Select>
                    </Grid>
                    {values.project_selection_config?.mode !== ProjectSelectionMode.DISABLED && (
                      <>
                        <Grid container gap={1} alignItems="center">
                          <Typography>{t('editOrderPage.projectSelection.count')}</Typography>
                          <Select
                            value={values.project_selection_config?.selection_count || ProjectSelectionCount.SINGLE}
                            onChange={(e) => setFieldValue('project_selection_config.selection_count', e.target.value)}
                            disabled={formIsDisabled}
                            size="medium">
                            <MenuItem value={ProjectSelectionCount.SINGLE}>
                              {t('editOrderPage.projectSelection.countSingle')}
                            </MenuItem>
                            <MenuItem value={ProjectSelectionCount.PER_PARTICIPANT}>
                              {t('editOrderPage.projectSelection.countPerParticipant')}
                            </MenuItem>
                          </Select>
                        </Grid>
                        <Grid container gap={1} alignItems="center">
                          <Typography>{t('editOrderPage.projectSelection.lockHours')}</Typography>
                          <FormTextField
                            isFieldUpdate
                            type="number"
                            width={70}
                            fieldName="project_selection_config.lock_hours_before_start"
                            disabled={formIsDisabled}
                          />
                          <Typography>{t('editOrderPage.projectSelection.hoursBeforeStart')}</Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </SettingsRow>
                {!isEventOrderPage && (
                  <SettingsRow label={t('editOrderPage.orderPageBuffer')}>
                    <FormTextField
                      width={100}
                      type="number"
                      isFieldUpdate
                      caption={t('editOrderPage.orderPageBufferCaption')}
                      fieldName="after_buffer_minutes"
                      disabled={formIsDisabled}
                    />
                  </SettingsRow>
                )}
                <SettingsRow label={t('editOrderPage.orderPageBufferDuration')}>
                  <FormTextField
                    width={100}
                    isFieldUpdate
                    fieldName="duration"
                    disabled={formIsDisabled}
                    type="number"
                  />
                </SettingsRow>
                <SettingsRow label={t('editOrderPage.waitlist')}>
                  <FormCheckbox
                    fieldName={'wait_list_enabled'}
                    disabled={formIsDisabled}
                    isFieldUpdate
                    label={t('editOrderPage.waitListEnabled')}
                  />
                </SettingsRow>
                <SettingsRow label={t('editOrderPage.contactDetails')} isLast={!isEventOrderPage}>
                  <FormCheckbox
                    fieldName="contact_details_optional_in_backoffice"
                    disabled={formIsDisabled}
                    isFieldUpdate
                    label={t('editOrderPage.contactDetailsOptionalInBackoffice')}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('editOrderPage.contactDetailsOptionalInBackofficeCaption')}
                  </Typography>
                </SettingsRow>
                {isEventOrderPage && (
                  <>
                    <SettingsRow label={t('editOrderPage.defaultTime')}>
                      <FormTimePicker isFieldUpdate fieldName="default_time" disabled={formIsDisabled} width={140} />
                    </SettingsRow>
                    <SettingsRow id="default-tickets-amount-row" label={t('editOrderPage.defaultTicketsAmount')} isLast>
                      <FormTextField
                        isFieldUpdate
                        type="number"
                        width={100}
                        fieldName="default_tickets_amount"
                        disabled={formIsDisabled}
                      />
                    </SettingsRow>
                  </>
                )}
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
